import { ref, onUnmounted } from 'vue'

// 与后端 PushEnvelope 对齐
export interface PushEnvelope<T = any> {
  user_id: number
  title?: string
  content?: string
  msg_type?: string
  business_id?: number
  extra_data?: string
  payload?: T
  created_at: number
}

type MessageHandler = (env: PushEnvelope) => void

export function useWebSocket(baseUrl: string) {
  const isConnected = ref(false)
  const lastMessage = ref<PushEnvelope | null>(null)

  let ws: WebSocket | null = null
  let reconnectTimer: number | null = null
  let heartbeatTimer: number | null = null
  let reconnectAttempts = 0
  const maxReconnectDelay = 30_000

  const handlers = new Map<string, Set<MessageHandler>>()

  const connect = () => {
    const data = localStorage.getItem('sys-v2.5.4-user')
    const token = data ? JSON.parse(data)?.accessToken : null
    if (!token) {
      console.warn('[WS] 未登录，跳过连接')
      return
    }

    // 浏览器 WebSocket 无法自定义 Header，token 走 query
    const url = `${baseUrl}?token=${encodeURIComponent(token)}`
    ws = new WebSocket(url)

    ws.onopen = () => {
      console.log('[WS] 已连接')
      isConnected.value = true
      reconnectAttempts = 0
      startHeartbeat()
    }

    ws.onmessage = (e) => {
      try {
        const env: PushEnvelope = JSON.parse(e.data)
        lastMessage.value = env
        // 按 msg_type 分发
        const type = env.msg_type || '*'
        handlers.get(type)?.forEach((h) => h(env))
        handlers.get('*')?.forEach((h) => h(env))
      } catch (err) {
        console.error('[WS] 解析消息失败', err, e.data)
      }
    }

    ws.onclose = () => {
      console.log('[WS] 连接关闭')
      isConnected.value = false
      stopHeartbeat()
      scheduleReconnect()
    }

    ws.onerror = (e) => {
      console.error('[WS] 错误', e)
    }
  }

  const scheduleReconnect = () => {
    if (reconnectTimer) return
    const delay = Math.min(1000 * 2 ** reconnectAttempts, maxReconnectDelay)
    reconnectAttempts++
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null
      connect()
    }, delay)
  }

  // 应用层心跳（后端自带 Ping/Pong，这里发一个文本包让后端也刷新心跳 Redis 键）
  const startHeartbeat = () => {
    stopHeartbeat()
    heartbeatTimer = window.setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping', ts: Date.now() }))
      }
    }, 30_000)
  }

  const stopHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
  }

  const disconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    stopHeartbeat()
    ws?.close()
    ws = null
  }

  /** 订阅某一类型消息，返回取消订阅函数；type 传 '*' 订阅全部 */
  const on = (type: string, handler: MessageHandler) => {
    if (!handlers.has(type)) handlers.set(type, new Set())
    handlers.get(type)!.add(handler)
    return () => handlers.get(type)?.delete(handler)
  }

  onUnmounted(() => disconnect())

  return { isConnected, lastMessage, connect, disconnect, on }
}